import React, { useState } from "react";
import { Box, Stack, Typography, TextField, Button, IconButton } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AppleIcon from "@mui/icons-material/Apple";
import logo from "../../assets/logo/logo.png";
import { useNavigate } from "react-router-dom";

// Navigation Items
const NAVIGATION_ITEMS = {
  home: "/",
  about: "/about",
  team: "/team",
  blog: "/blog",
  contact: "/contact",
  terms: "/terms",
};

// Social Links
const INSTAGRAM_URL = "https://www.instagram.com/moipay.lk?igsh=MWQ4dmc2Z3l3MDhxcQ==";
const YOUTUBE_URL = "https://youtube.com/@moipay-d3n?si=XWFXKjTnNhO3NgRl";
const LINKEDIN_URL = "https://www.linkedin.com/company/moipay-lk/";

// Store Links
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.rubaai.lk";
const APP_STORE_URL = "https://apps.apple.com/lk/app/moipay/id6742509254";

// Footer Link Component
const FooterLink = ({ text, onClick }) => (
  <Typography
    onClick={onClick}
    sx={{ cursor: "pointer", opacity: 0.9, "&:hover": { opacity: 1 } }}
  >
    {text}
  </Typography>
);

// Quick Links Component
const QuickLinksSection = () => {
  const navigate = useNavigate();

  const handleNavigation = (path) => navigate(path);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, fontSize: "28px", color: "#fff" }}
      >
        Quick Links
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <FooterLink text="Home" onClick={() => handleNavigation(NAVIGATION_ITEMS.home)} />
        <FooterLink text="About Us" onClick={() => handleNavigation(NAVIGATION_ITEMS.about)} />
        <FooterLink text="Our Team" onClick={() => handleNavigation(NAVIGATION_ITEMS.team)} />
        <FooterLink text="Blogs" onClick={() => handleNavigation(NAVIGATION_ITEMS.blog)} />
        <FooterLink text="Contact Us" onClick={() => handleNavigation(NAVIGATION_ITEMS.contact)} />
        <FooterLink text="Terms & Conditions" onClick={() => handleNavigation(NAVIGATION_ITEMS.terms)} />
      </Box>
    </Box>
  );
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubscribe = async () => {
  if (!email) {
    setMessage("Please enter your email.");
    return;
  }

  // Simple email check (extra safety)
  if (!email.includes("@")) {
    setMessage("Please enter a valid email address.");
    return;
  }

  try {
    const response = await fetch(
      "https://api-23va3juqbq-el.a.run.app/api/website/subscriptions",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      }
    );

    if (response.ok) {
      setMessage("Thank you! You are successfully subscribed 💙");
      setEmail("");
    } else {
      setMessage("You're already on the list! You're all set to receive our latest updates");
    }
  } catch (error) {
    console.error("Newsletter error:", error);
    setMessage("Network error. Please check your internet.");
  }
};

  return (
    <Box
  id="download-section"
  sx={{ width: "100%", background: "#000", color: "#fff", p: 6, borderTop: "1px solid #333333" }}
>

      <Stack direction={{ xs: "column", md: "row" }} spacing={8} justifyContent="space-between">

        {/* Left Section */}
        <Stack spacing={3} maxWidth="420px">
          <img src={logo} alt="MOI PAY" width={120} />
        </Stack>

        {/* Middle About Section */}
        <Stack spacing={3} maxWidth="420px">
          <Typography sx={{ fontSize: "28px", fontWeight: 600 }}>
            Simplify Your Financial World
          </Typography>
          <Typography sx={{ fontSize: "18px", opacity: 0.85, lineHeight: 1.6 }}>
            Our global mission is for every person and business to do all things money spending, saving, investing, borrowing, managing, and more in just a few taps.
          </Typography>
          {/* Social Icons */}
          <Stack direction="row" spacing={2}>
            <IconButton component="a" href={INSTAGRAM_URL} target="_blank">
              <InstagramIcon sx={{ fontSize: 40, color: "#FF0069" }} />
            </IconButton>
            <IconButton component="a" href={YOUTUBE_URL} target="_blank">
              <YouTubeIcon sx={{ fontSize: 40, color: "#FF0000" }} />
            </IconButton>
            <IconButton component="a" href={LINKEDIN_URL} target="_blank">
              <LinkedInIcon sx={{ fontSize: 40, color: "#0077B5" }} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Quick Links */}
        <QuickLinksSection />

        {/* Newsletter */}
        <Stack spacing={3} maxWidth="500px">
          <Typography sx={{ fontSize: "28px", fontWeight: 600 }}>Never Miss an Update</Typography>
          <Typography>Your Email</Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              placeholder="Enter Your Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ background: "#333", borderRadius: "6px" }}
              InputProps={{ sx: { color: "#fff" } }}
            />
            <Button sx={{ background: "#3A82F7", color: "#fff", px: 3 }} onClick={handleSubscribe}>
              Subscribe
            </Button>
          </Stack>
          {message && <Typography sx={{ color: "#3A82F7", mt: 1 }}>{message}</Typography>}

          {/* Store Buttons */}
          <Stack direction="row" spacing={3}>
            <Button
              variant="outlined"
              component="a"
              href={PLAY_STORE_URL}
              target="_blank"
              sx={{ borderColor: "#3A82F7", color: "#fff", px: 4, borderRadius: "40px" }}
              startIcon={<PlayArrowIcon />}
            >
              Play Store
            </Button>
            <Button
              variant="outlined"
              component="a"
              href={APP_STORE_URL}
              target="_blank"
              sx={{ borderColor: "#3A82F7", color: "#fff", px: 4, borderRadius: "40px" }}
              startIcon={<AppleIcon />}
            >
              App Store
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {/* Bottom Line */}
      <Box sx={{ width: "100%", height: "0.6px", background: "#333", my: 4 }} />

      {/* Footer Bottom */}
      <Typography textAlign="center" sx={{ opacity: 0.9 }}>
        ©2025 Rubaai. Created by <strong>Rubaai Technologies Pvt Ltd</strong>
      </Typography>
    </Box>
  );
}


